// Mapa estático de regiões metropolitanas.
// Cidade-base (chave) → cidades que compõem sua região metropolitana.
// Para adicionar uma nova região (ex.: Rio, BH), inclua uma nova entrada aqui.

const REGIOES: Record<string, string[]> = {
  "São Paulo": [
    "Santo André",
    "São Bernardo do Campo",
    "São Bernardo",
    "S. Bernardo",
    "São. Bernardo",
    "São Caetano do Sul",
    "São Caetano",
    "Diadema",
    "Mauá",
    "Ribeirão Pires",
    "Rio Grande da Serra",
    "Guarulhos",
    "Osasco",
    "Barueri",
    "Carapicuíba",
    "Cotia",
    "Taboão da Serra",
    "Embu das Artes",
    "Itapecerica da Serra",
    "Suzano",
    "Mogi das Cruzes",
    "Itaquaquecetuba",
    "Poá",
    "Ferraz de Vasconcelos",
  ],
};

// Valores de `bairro` que na verdade são regiões, não bairros de fato.
// Chave = cidade normalizada; valor = lista de bairros normalizados a
// tratar como "região metropolitana" em vez de bairro real.
const NAO_BAIRROS_POR_CIDADE: Record<string, string[]> = {
  "sao paulo": ["abc"],
};

function norm(s: string | null | undefined): string {
  return (s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Retorna a cidade-base cuja região metropolitana contém `cidade`.
 * Se `cidade` for a própria cidade-base, retorna ela mesma.
 * Caso não encontre, retorna null.
 */
export function getRegiaoMetropolitana(cidade: string | null | undefined): string | null {
  const c = norm(cidade);
  if (!c) return null;
  for (const [base, vizinhas] of Object.entries(REGIOES)) {
    if (norm(base) === c) return base;
    if (vizinhas.some((v) => norm(v) === c)) return base;
  }
  return null;
}

/**
 * True se `outraCidade` está na região metropolitana da `cidadeBase`.
 */
export function isNaRegiaoMetropolitana(
  cidadeBase: string | null | undefined,
  outraCidade: string | null | undefined,
): boolean {
  const base = norm(cidadeBase);
  const other = norm(outraCidade);
  if (!base || !other) return false;
  const vizinhas = REGIOES[cidadeBase ?? ""] ?? Object.entries(REGIOES).find(([k]) => norm(k) === base)?.[1];
  if (!vizinhas) return false;
  return vizinhas.some((v) => norm(v) === other);
}

/**
 * Lista de cidades da região metropolitana de uma cidade-base (normalizadas para comparação).
 */
export function getVizinhasNormalizadas(cidadeBase: string | null | undefined): string[] {
  const base = norm(cidadeBase);
  const entry = Object.entries(REGIOES).find(([k]) => norm(k) === base);
  if (!entry) return [];
  return entry[1].map(norm);
}

/**
 * True quando `bairro` é um bairro de verdade (não vazio, diferente da
 * própria cidade, e não faz parte da lista de "não-bairros" da cidade).
 */
export function isBairroReal(
  cidade: string | null | undefined,
  bairro: string | null | undefined,
): boolean {
  const b = norm(bairro);
  if (!b) return false;
  const c = norm(cidade);
  if (c && b === c) return false;
  const naoBairros = NAO_BAIRROS_POR_CIDADE[c] ?? [];
  if (naoBairros.includes(b)) return false;
  return true;
}

export { norm as normalizeCidade };